import { isAxiosError } from "axios";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAmountsStore } from "@/features/auth/amounts-store";
import { useVerifyAmounts } from "@/features/auth/hooks";
import { apiErrorMessage } from "@/lib/api/errors";

/**
 * Fenêtre unique de déverrouillage des montants (re-saisie du mot de passe,
 * `POST /auth/verify-amounts/`). Montée une fois dans le layout protégé ;
 * ouverte par `useAmountsStore().requestUnlock()` depuis l'œil de la navbar
 * ou l'œil placé devant chaque montant masqué (`MaskedMoney`).
 */
export function AmountsUnlockDialog() {
  const open = useAmountsStore((s) => s.unlockOpen);
  const close = useAmountsStore((s) => s.closeUnlock);
  const setVisible = useAmountsStore((s) => s.setVisible);
  const verify = useVerifyAmounts();

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPassword("");
      setShowPassword(false);
      setError(null);
    }
  }, [open]);

  const confirm = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    verify.mutate(password, {
      onSuccess: () => {
        setVisible(true);
        close();
        setPassword("");
        toast.success("Montants affichés.");
      },
      onError: (err) => {
        if (isAxiosError(err) && err.response?.status === 401) {
          setError("Mot de passe incorrect.");
          return;
        }
        // 403 (permission retirée entre-temps) ou erreur réseau : toast générique.
        toast.error(apiErrorMessage(err, "Impossible d'afficher les montants."));
        close();
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Afficher les montants</DialogTitle>
          <DialogDescription>
            Confirmez votre mot de passe pour afficher les montants dans l'application.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={confirm}>
          <DialogBody className="space-y-1.5">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <Label htmlFor="amounts-password">Mot de passe</Label>
              {error && <p className="ml-auto text-xs leading-tight text-destructive">{error}</p>}
            </div>
            <div className="relative">
              <Input
                id="amounts-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                autoFocus
                aria-invalid={!!error}
                className="pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted transition-colors hover:text-muted-foreground"
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              Annuler
            </Button>
            <Button type="submit" disabled={verify.isPending || !password}>
              {verify.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirmer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
